import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  useMsal,
  useIsAuthenticated,
  useAccount,
  MsalProvider,
} from '@azure/msal-react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { msalInstance, loginRequest } from './msal';
import { acquireTokenDeduped, isInteractionInProgress } from './authToken';
// [split W1-B] Sign-in branding: a tenant custom domain shows that tenant's
// host-resolved branding; the platform's own host shows the PLATFORM name
// (PLATFORM_NAME), never a tenant's brand.
import { useSignInBranding } from './platformIdentity';
import {
  googleEnabled, getGoogleSession, subscribeGoogle, getGoogleToken,
  signOutGoogle, renderGoogleButton, GoogleSession,
} from './googleAuth';

/**
 * Clerk-shaped auth shim over MSAL.
 *
 * The 9 frontend files that used @clerk/clerk-react continue to import
 * useAuth / useUser / useClerk / SignIn / RedirectToSignIn — from here —
 * and keep working with no code changes beyond the import path swap.
 *
 * Why shim instead of "just use MSAL directly":
 *   - Clerk's data shape is deeply entangled with 9 files across the SPA
 *     (user.firstName, user.primaryEmailAddress.emailAddress,
 *      user.publicMetadata.role, user.imageUrl…). Rewriting each of those
 *     to MSAL's AccountInfo shape = 50+ line-level edits + risk of
 *     introducing bugs in the RBAC flow.
 *   - The shim fits in ~250 LOC and is the single place to fix if we ever
 *     want to change the auth provider again.
 *
 * Role resolution:
 *   MSAL only carries Azure-level claims (oid, email, name). Our app-level
 *   role lives in the Postgres `users` table. After login we fetch it once
 *   from /api/v1/admin/whoami and expose it as user.publicMetadata.role so
 *   RBACContext keeps working with zero changes.
 */

// ─── Export the MSAL provider directly so main.tsx can wrap the app ─────
export { MsalProvider };
export { msalInstance };

// ─── Demo-deployment mode ────────────────────────────────────────────────
// On a VITE_DEMO_MODE=1 build there is no Microsoft sign-in: the hooks below
// short-circuit to a fixed "Demo Presenter" identity and getToken returns the
// demo bearer token the demo backend recognises. IS_DEMO_BUILD is a
// build-time constant, so the early returns never change between renders
// (safe with the rules of hooks).
import { IS_DEMO_BUILD, demoBearerToken, clearDemoAccessCode } from './demoMode';

const DEMO_OID = 'demo-visitor';

const DEMO_AUTH: UseAuthReturn = {
  isLoaded: true,
  isSignedIn: true,
  userId: DEMO_OID,
  getToken: async () => demoBearerToken(),
  signOut: async () => {
    clearDemoAccessCode();
    window.location.href = '/';
  },
};

const DEMO_USER: ClerkShapedUser = {
  id: DEMO_OID,
  firstName: 'Demo',
  lastName: 'Presenter',
  fullName: 'Demo Presenter',
  imageUrl: '',
  primaryEmailAddress: { emailAddress: 'demo.presenter@demo.fnsai.local' },
  emailAddresses: [{ emailAddress: 'demo.presenter@demo.fnsai.local' }],
  publicMetadata: { role: 'ceo' },
};

// ─── Shape definitions that mirror Clerk's ClerkUser ────────────────────
export interface ClerkShapedUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  imageUrl: string;
  primaryEmailAddress: { emailAddress: string } | null;
  emailAddresses: Array<{ emailAddress: string }>;
  publicMetadata: { role?: string };
}

// ─── Whoami cache (fetched once per session per account) ───────────────
// The fetch happens inside useUser, but we cache at module level so
// every component that calls useUser shares the same state (otherwise
// each would refetch). Keyed by Azure oid.
type WhoamiCacheEntry = {
  promise: Promise<{ role?: string; email?: string; name?: string } | null>;
  value: { role?: string; email?: string; name?: string } | null;
};
const whoamiCache = new Map<string, WhoamiCacheEntry>();

function fetchWhoami(getToken: () => Promise<string | null>): Promise<{
  role?: string;
  email?: string;
  name?: string;
} | null> {
  return getToken()
    .then(async (token) => {
      if (!token) return null;
      const base = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
      const res = await axios.get(`${base}/api/v1/admin/whoami`, {
        headers: { Authorization: `Bearer ${token}` },
        validateStatus: () => true,
      });
      if (res.status !== 200) return null;
      return {
        role: res.data.role_in_db ?? undefined,
        email: res.data.email ?? undefined,
        name: res.data.name ?? undefined,
      };
    })
    .catch(() => null);
}

function splitName(full: string | null | undefined): { first: string | null; last: string | null } {
  if (!full) return { first: null, last: null };
  const parts = full.trim().split(/\s+/);
  if (parts.length === 0) return { first: null, last: null };
  if (parts.length === 1) return { first: parts[0], last: null };
  return { first: parts[0], last: parts.slice(1).join(' ') };
}

/**
 * Begin the Microsoft redirect.
 *
 * `extraQueryParameters` carries `domain_hint` when the user has named their
 * company, which sends them to their own organisation's sign-in rather than the
 * generic Microsoft account picker. It is a hint to Microsoft, not a claim we
 * trust: the backend still checks which directory the returned token came from.
 */
export function startMicrosoftSignIn(extraQueryParameters?: Record<string, string>): void {
  const request = { ...loginRequest, extraQueryParameters };
  msalInstance.loginRedirect(request).catch((err) => {
    // Same stale interaction_in_progress flag handled in RedirectToSignIn — a
    // tab closed mid-redirect leaves it set and blocks every later attempt.
    if (isInteractionInProgress(err)) {
      void msalInstance
        .clearCache()
        .then(() => msalInstance.loginRedirect(request))
        .catch((retryErr) => console.error('[auth] sign-in failed after cache clear:', retryErr));
      return;
    }
    console.error('[auth] sign-in redirect failed:', err);
  });
}

// ─── Token acquisition ───────────────────────────────────────────────────
// Lives in ./authToken so the recovery path (dedupe + redirect-based
// re-auth) can be unit-tested without mounting React.

// ─── useAuth — Clerk-compat ───────────────────────────────────────────────
export interface UseAuthReturn {
  isLoaded: boolean;
  isSignedIn: boolean;
  userId: string | null;
  getToken: (opts?: { forceRefresh?: boolean }) => Promise<string | null>;
  signOut: () => Promise<void>;
}

// Subscribe to the Google session store (module-level, updated by GIS
// callbacks). Always called so hook order is stable; returns null when
// Google is disabled or nobody is signed in with Google.
function useGoogleSession(): GoogleSession | null {
  const [session, setSession] = useState<GoogleSession | null>(() => getGoogleSession());
  useEffect(() => {
    if (!googleEnabled()) return;
    return subscribeGoogle(() => setSession(getGoogleSession()));
  }, []);
  return googleEnabled() ? session : null;
}

export function useAuth(): UseAuthReturn {
  // Demo build: constant identity, no MSAL. IS_DEMO_BUILD never changes at
  // runtime, so this early return is stable across renders.
  if (IS_DEMO_BUILD) return DEMO_AUTH;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { accounts, inProgress } = useMsal();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const account = useAccount(accounts[0] ?? null);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const google = useGoogleSession();

  // A Microsoft account takes precedence if present; otherwise a Google
  // session signs the user in. One person uses one provider, so they don't
  // normally coexist.
  const isSignedIn = !!account || !!google;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const getToken = useCallback(async (opts?: { forceRefresh?: boolean }): Promise<string | null> => {
    if (account) return acquireTokenDeduped(account, opts?.forceRefresh ?? false);
    if (google) return getGoogleToken(); // null when expired → app re-auths
    return null;
  }, [account, google]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const signOut = useCallback(async () => {
    if (google && !account) {
      signOutGoogle();
      window.location.href = window.location.origin;
      return;
    }
    await msalInstance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  }, [account, google]);

  return {
    isLoaded: inProgress !== 'startup',
    isSignedIn,
    userId: account?.homeAccountId?.split('.')[0] ?? google?.sub ?? null,
    getToken,
    signOut,
  };
}

// ─── useUser — Clerk-compat (fetches DB role via /whoami) ────────────────
export interface UseUserReturn {
  isLoaded: boolean;
  isSignedIn: boolean;
  user: ClerkShapedUser | null;
}

export function useUser(): UseUserReturn {
  if (IS_DEMO_BUILD) return { isLoaded: true, isSignedIn: true, user: DEMO_USER };
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { accounts, inProgress } = useMsal();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const account = useAccount(accounts[0] ?? null);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const google = useGoogleSession();
  const { getToken } = useAuth();

  // A stable per-identity key for the whoami cache: Microsoft account id,
  // else the Google sub, else null.
  const identityKey = account?.homeAccountId ?? (google ? `google:${google.sub}` : null);

  // Local mirror of the whoami result. Updated once the cached fetch
  // resolves. Starts null; RBAC consumers handle `role === null` by
  // treating it as "loading" (RBACProvider already does).
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [whoami, setWhoami] = useState<{ role?: string; email?: string; name?: string } | null>(
    () => (identityKey ? whoamiCache.get(identityKey)?.value ?? null : null)
  );
  // Track the key we're currently subscribed to so the effect only fires
  // once per identity change.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const subscribedRef = useRef<string | null>(null);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!identityKey) {
      setWhoami(null);
      subscribedRef.current = null;
      return;
    }
    if (subscribedRef.current === identityKey) return;
    subscribedRef.current = identityKey;

    const cached = whoamiCache.get(identityKey);
    if (cached?.value) {
      setWhoami(cached.value);
      return;
    }
    const promise = cached?.promise ?? fetchWhoami(getToken);
    whoamiCache.set(identityKey, { promise, value: cached?.value ?? null });
    promise.then((value) => {
      whoamiCache.set(identityKey, { promise, value });
      if (subscribedRef.current === identityKey) setWhoami(value);
    });
  }, [identityKey, getToken]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const user = useMemo<ClerkShapedUser | null>(() => {
    // Microsoft account first, then Google session.
    if (account) {
      const displayName = whoami?.name ?? account.name ?? null;
      const { first, last } = splitName(displayName);
      const email = whoami?.email ?? account.username ?? null;
      const oid = account.homeAccountId.split('.')[0];
      return {
        id: oid, firstName: first, lastName: last, fullName: displayName, imageUrl: '',
        primaryEmailAddress: email ? { emailAddress: email } : null,
        emailAddresses: email ? [{ emailAddress: email }] : [],
        publicMetadata: { role: whoami?.role },
      };
    }
    if (google) {
      const displayName = whoami?.name ?? google.name ?? null;
      const { first, last } = splitName(displayName);
      const email = whoami?.email ?? google.email ?? null;
      return {
        id: google.sub, firstName: first, lastName: last, fullName: displayName, imageUrl: '',
        primaryEmailAddress: email ? { emailAddress: email } : null,
        emailAddresses: email ? [{ emailAddress: email }] : [],
        publicMetadata: { role: whoami?.role },
      };
    }
    return null;
  }, [account, google, whoami]);

  return {
    isLoaded: inProgress !== 'startup',
    isSignedIn: !!account || !!google,
    user,
  };
}

// ─── useClerk — Clerk-compat (signOut + openUserProfile) ────────────────
export interface UseClerkReturn {
  signOut: (opts?: { redirectUrl?: string }) => Promise<void>;
  openUserProfile: () => void;
}

export function useClerk(): UseClerkReturn {
  if (IS_DEMO_BUILD) {
    return {
      signOut: async () => { clearDemoAccessCode(); window.location.href = '/'; },
      openUserProfile: () => { /* no profile on the demo site */ },
    };
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const signOut = useCallback(async (opts?: { redirectUrl?: string }) => {
    // Google-only session: clear it and go home (no MSAL logout to run).
    if (googleEnabled() && getGoogleSession() && msalInstance.getAllAccounts().length === 0) {
      signOutGoogle();
      window.location.href = opts?.redirectUrl
        ? new URL(opts.redirectUrl, window.location.origin).toString()
        : window.location.origin;
      return;
    }
    await msalInstance.logoutRedirect({
      postLogoutRedirectUri: opts?.redirectUrl
        ? new URL(opts.redirectUrl, window.location.origin).toString()
        : window.location.origin,
    });
  }, []);

  const openUserProfile = useCallback(() => {
    // Azure doesn't have an in-app profile widget. Open the Microsoft
    // account page in a new tab — closest UX match to Clerk's modal.
    window.open('https://myaccount.microsoft.com/', '_blank', 'noopener');
  }, []);

  return { signOut, openUserProfile };
}

// ─── <SignIn /> — Clerk-compat login card ───────────────────────────────
// Mimics Clerk's SignIn component — a card with a "Sign in" button that
// triggers the MSAL popup/redirect flow. Supports the `routing="hash"`
// prop for API compat, even though MSAL handles routing internally.
export function SignIn(_props: { routing?: string }): React.ReactElement {
  const brand = useSignInBranding();
  const [busy, setBusy] = useState(false);
  const [googleErr, setGoogleErr] = useState<string | null>(null);
  const googleBtnRef = useRef<HTMLDivElement | null>(null);

  const handleClick = async () => {
    setBusy(true);
    try {
      await msalInstance.loginRedirect(loginRequest);
    } catch (err) {
      console.error('[auth] sign-in failed:', err);
      setBusy(false);
    }
  };

  // Render the official Google button when Google sign-in is configured.
  // Entirely skipped (no script load, no UI) when VITE_GOOGLE_CLIENT_ID is
  // unset — the card is identical to the Microsoft-only version.
  useEffect(() => {
    if (!googleEnabled() || !googleBtnRef.current) return;
    let cancelled = false;
    renderGoogleButton(googleBtnRef.current).catch((err) => {
      if (!cancelled) setGoogleErr('Google sign-in is temporarily unavailable.');
      console.error('[auth] google button render failed:', err);
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        padding: 32,
        minWidth: 360,
        boxShadow: '0 8px 32px rgba(0,0,0,0.24)',
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#1e293b' }}>
        Sign in to {brand.app_name}
      </div>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 24 }}>
        {googleEnabled()
          ? 'Sign in with your work account.'
          : 'Sign in with your Microsoft work account.'}
      </div>
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        style={{
          width: '100%',
          padding: '12px 16px',
          background: '#2f2f2f',
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 500,
          cursor: busy ? 'wait' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
        }}
      >
        <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden>
          <rect x="1"  y="1"  width="9" height="9" fill="#F25022" />
          <rect x="11" y="1"  width="9" height="9" fill="#7FBA00" />
          <rect x="1"  y="11" width="9" height="9" fill="#00A4EF" />
          <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
        </svg>
        {busy ? 'Signing in…' : 'Sign in with Microsoft'}
      </button>

      {googleEnabled() && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '18px 0' }}>
            <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
            <span style={{ fontSize: 11, color: '#94a3b8' }}>or</span>
            <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
          </div>
          {/* GIS renders its official button inside this container. */}
          <div ref={googleBtnRef} style={{ display: 'flex', justifyContent: 'center' }} />
          {googleErr && <div style={{ marginTop: 10, fontSize: 12, color: '#b91c1c' }}>{googleErr}</div>}
        </>
      )}
    </div>
  );
}

// ─── <RedirectToSignIn /> — Clerk-compat ────────────────────────────────
// Mimics Clerk's RedirectToSignIn. On mount, kicks the MSAL redirect flow.
//
// Two bugs informed the current shape:
//
//  1. PR #32 — used to return `null`, so when loginRedirect() failed
//     silently (popup blocker, 3rd-party cookies, network blip) the
//     user got a blank page. Now renders visible UI always, with an
//     8s fallback after which an explicit retry button appears.
//
//  2. *This* fix — the previous version called loginRedirect() the
//     moment isAuthed was false, even when MSAL's inProgress state
//     was still 'startup' (booting) or 'handleRedirect' (processing
//     the response from Microsoft). Calling loginRedirect() during
//     handleRedirect throws MSAL into a loop: navigate to MS → come
//     back → render → fire redirect again before MSAL finishes
//     handling the previous one → user is stuck on "Redirecting…".
//     Now we only call loginRedirect when MSAL's queue is genuinely
//     empty (`inProgress === 'none'`), and we don't start the
//     "stuck" timer until that gate has passed — so a slow
//     handleRedirect never false-positives the fallback.
//
//  4. *This* fix — the previous version started the stuck-timer
//     ONLY after a successful loginRedirect call. If MSAL was wedged
//     in 'startup' or 'login' indefinitely (stale cached account
//     with bad token, Azure config issue, network drop mid-init),
//     the gate `if (inProgress !== 'none') return` blocked the call
//     forever and the timer never started — splash forever, no
//     escape. The QA report ("stuck on Checking your sign-in…")
//     showed exactly that. Now the stuck-timer runs unconditionally
//     from mount, and the recovery card includes a "Sign out & try
//     again" button that hard-clears the MSAL cache when a stale
//     account is the root cause.
// Splash escape timers. The "soft" message nudges the user that
// something's slow (often Railway cold-starting the backend); the
// "stuck" card then offers the full recovery options.
//
// History of tuning:
//   10s → 6s: QA report "Checking your sign-in… hangs forever"
//   6s → 20s: 6s was firing as a FALSE positive — Railway cold-start
//     + Microsoft handshake routinely takes 8-12s. Users were
//     clicking "Sign in" on the recovery card and aborting a
//     legitimate redirect that was about to succeed. Esther
//     specifically called this out: "I keep having to sign in."
//     20s is well past the longest legitimate flow we've measured
//     (~14s on a cold Railway service), so the recovery card now
//     only appears for genuinely-stuck states.
//   The soft hint moved 4s → 8s for the same reason — at 4s the
//     "Taking a bit longer than usual…" line was flashing for
//     every normal sign-in and looked broken.
const SPLASH_SOFT_MS      = 8_000;
const REDIRECT_TIMEOUT_MS = 20_000;

export function RedirectToSignIn(): React.ReactElement {
  const isAuthed = useIsAuthenticated();
  const { inProgress } = useMsal();
  const google = useGoogleSession();
  // When Google sign-in is enabled we must NOT auto-redirect to Microsoft —
  // that would deny the user their provider choice. Show the sign-in card
  // (both buttons) centered instead. Google-only session state is handled
  // by the app's own auth gating; here we just present the options.
  //
  // This is computed, not returned on, because returning here would sit
  // ABOVE the hooks below. The moment the condition flipped — the user
  // picks Google and `google` becomes non-null — React would see more
  // hooks than on the previous render and throw "Rendered more hooks than
  // during the previous render", blanking the page at the exact instant
  // they signed in. Every hook runs unconditionally; the branch is taken
  // after them.
  const showProviderChoice = googleEnabled() && !isAuthed && !google;

  const [error, setError] = React.useState<Error | null>(null);
  const [stuck, setStuck] = React.useState(false);
  const [slow, setSlow] = React.useState(false);
  // Track whether we've ever actually fired loginRedirect so the retry
  // button knows whether the "Sign in" click should be treated as a
  // first attempt or a retry of an already-tried flow.
  const attemptedRef = React.useRef(false);
  // Does MSAL already have a cached account on disk? If yes, we should
  // wait for MSAL to use it (silent SSO) instead of forcing a fresh
  // interactive redirect — that wait is the difference between
  // "signed back in invisibly" and "had to click Microsoft again".
  const hasCachedAccount = React.useMemo(
    () => msalInstance.getAllAccounts().length > 0,
    []
  );

  const startRedirect = React.useCallback((opts?: { force?: boolean }) => {
    setError(null);
    setStuck(false);
    attemptedRef.current = true;
    // The user clicking "Sign in" on the stuck card means "try again
    // even if MSAL thinks it's busy" — without bypassing the inProgress
    // gate here, a wedged MSAL state would keep the call from firing.
    if (opts?.force === false) {
      // (No-op — only mentioned for symmetry with the inProgress gate
      // in the effect below.)
    }
    msalInstance.loginRedirect(loginRequest).catch((err) => {
      // MSAL keeps an "interaction in progress" flag in storage. A tab closed
      // mid-redirect (or a crash) leaves it set with nothing actually running,
      // and every later sign-in attempt bounces off it — the user is locked
      // out until they clear site data by hand. Clearing the cache is the
      // documented remedy, so do it for them and retry once.
      if (isInteractionInProgress(err)) {
        void msalInstance
          .clearCache()
          .then(() => msalInstance.loginRedirect(loginRequest))
          .catch((retryErr) => {
            console.error('[auth] RedirectToSignIn failed after cache clear:', retryErr);
            setError(retryErr instanceof Error ? retryErr : new Error(String(retryErr)));
          });
        return;
      }
      console.error('[auth] RedirectToSignIn failed:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
    });
  }, []);

  // "Sign out & try again" — clears MSAL's cache so a stale cached
  // account can't keep silently refreshing into a 401 loop. The
  // logoutRedirect call navigates the user, so we don't need to
  // setState afterwards.
  const startCleanSignIn = React.useCallback(() => {
    msalInstance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    }).catch((err) => {
      console.error('[auth] logoutRedirect failed:', err);
      // If even logout fails, force a hard reload — drops the SPA's
      // memory state at least.
      window.location.reload();
    });
  }, []);

  useEffect(() => {
    // Two-stage timer. After SPLASH_SOFT_MS we surface a "still
    // checking…" hint so the user knows we're alive; after
    // REDIRECT_TIMEOUT_MS the full recovery card appears.
    const soft = setTimeout(() => setSlow(true), SPLASH_SOFT_MS);
    const hard = setTimeout(() => setStuck(true), REDIRECT_TIMEOUT_MS);
    return () => { clearTimeout(soft); clearTimeout(hard); };
  }, []);

  useEffect(() => {
    // Offering a provider choice means the user has not picked one yet —
    // firing the Microsoft redirect here would pick for them.
    if (showProviderChoice) return;
    if (isAuthed) return;
    // Only call loginRedirect when MSAL is genuinely idle. Firing it
    // during 'startup' or 'handleRedirect' produces the redirect-loop
    // ("stuck on Redirecting…") that surfaced after PR #32 deployed.
    if (inProgress !== 'none') return;
    if (attemptedRef.current) return; // already kicked off — don't retry every render
    // If MSAL has a cached account, try silent SSO first. ssoSilent
    // uses the refresh-token / Azure cookie to log the user back in
    // without ANY UI flash, the way "stay signed in" feels in
    // Outlook.com. Only fall through to a visible loginRedirect if
    // silent SSO fails — that path is what was firing every time
    // before and showing Microsoft's interactive flow unnecessarily.
    if (hasCachedAccount) {
      // Re-read accounts at effect time — useMemo snapshotted at mount,
      // and MSAL could have cleared the cache between mount and here
      // (e.g. user clicked "Sign out & try again" in a fast double-flow).
      // Without this guard account.username would NPE.
      const account = msalInstance.getAllAccounts()[0];
      if (account) {
        attemptedRef.current = true;
        // Race ssoSilent against an 8s timeout. Without this, a hung
        // ssoSilent call (Azure latency, network stall) would never
        // resolve — neither .then nor .catch fires — and the splash
        // sits forever. After 8s we fall through to visible redirect
        // which is guaranteed to make UI progress.
        const SSO_SILENT_TIMEOUT_MS = 8_000;
        const timeout = new Promise<never>((_resolve, reject) => {
          setTimeout(() => reject(new Error('ssoSilent timeout')), SSO_SILENT_TIMEOUT_MS);
        });
        Promise.race([
          msalInstance.ssoSilent({ ...loginRequest, account, loginHint: account.username }),
          timeout,
        ]).catch(() => {
          // Silent failed or timed out. Fall through to visible redirect.
          startRedirect();
        });
        return;
      }
    }
    startRedirect();
  }, [showProviderChoice, isAuthed, inProgress, startRedirect, hasCachedAccount]);

  // Provider choice — rendered after every hook has run (see the note on
  // showProviderChoice above).
  if (showProviderChoice) {
    return (
      <div style={signInFallbackWrap}>
        <SignIn routing="hash" />
      </div>
    );
  }

  // If the user landed on /sign-in/* but is already authenticated (e.g.
  // axios redirected here from a transient 401 even though MSAL still
  // has their token), bounce them home instead of sitting on the
  // "Checking your sign-in…" splash indefinitely. This was Esther's
  // 10-minute hang: cached account + on /sign-in/* + isAuthed=true
  // meant the effect above early-returned and nothing transitioned the
  // splash, so the user was stuck until the 20s stuck-timer fired.
  if (isAuthed) {
    return <Navigate to="/" replace />;
  }

  // If something definitive went wrong OR we've been waiting too long,
  // give the user actionable UI instead of a blank screen.
  if (error || stuck) {
    return (
      <div style={signInFallbackWrap}>
        <div style={signInFallbackCard}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 8 }}>
            {error ? 'Sign-in error' : 'Taking longer than expected'}
          </div>
          <h1 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 700, color: '#111827' }}>
            {error ? "We couldn't reach Microsoft sign-in." : "Still redirecting…"}
          </h1>
          <p style={{ margin: '0 0 16px', fontSize: 14, color: '#475569', lineHeight: 1.55 }}>
            {error
              ? "This is usually a popup blocker, third-party cookies disabled, or a network blip. Click below to retry."
              : "If nothing happens, your browser may be blocking the redirect. Click below to try again."}
          </p>
          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: 8, padding: '10px 12px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12, color: '#991b1b', marginBottom: 16, wordBreak: 'break-word' }}>
              {error.message}
            </div>
          )}
          <p style={{ margin: '8px 0 14px', fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
            If clicking <strong>Sign in</strong> doesn't help, your previous session may be stuck.
            Use <strong>Sign out &amp; try again</strong> to clear it and start over.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* The retry button has to reset attemptedRef so the
                effect treats this as a fresh attempt — without that,
                the gate above (`if (attemptedRef.current) return`)
                would block subsequent retries. force=true also tells
                startRedirect to bypass the inProgress gate so a
                wedged MSAL state doesn't block the click. */}
            <button
              onClick={() => { attemptedRef.current = false; startRedirect({ force: true }); }}
              style={signInFallbackBtn}>
              Sign in
            </button>
            <button onClick={startCleanSignIn} style={signInFallbackBtnGhost}>
              Sign out &amp; try again
            </button>
            <button onClick={() => window.location.reload()} style={signInFallbackBtnGhost}>
              Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Splash. Label differentiates between "MSAL is still booting /
  // processing the response from Microsoft" (waiting on inProgress)
  // and "we just fired the redirect" (browser is leaving) so the user
  // can tell whether something's happening.
  const splashLabel = inProgress === 'none' && attemptedRef.current
    ? 'Redirecting to sign in…'
    : 'Checking your sign-in…';
  return (
    <div style={signInFallbackWrap}>
      <div style={{ ...signInFallbackCard, textAlign: 'center' }}>
        <div style={{ width: 32, height: 32, margin: '0 auto 14px', border: '3px solid #e5e7eb', borderTopColor: '#1565c0', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <div style={{ fontSize: 14, fontWeight: 600, color: '#1f2937' }}>{splashLabel}</div>
        {slow && (
          <>
            <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>
              Taking a bit longer than usual — the backend may be waking up.
            </div>
            <button
              onClick={() => window.location.reload()}
              style={{ marginTop: 12, padding: '6px 12px', background: '#f5f7fa', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#444' }}
            >
              Reload
            </button>
          </>
        )}
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}

const signInFallbackWrap: React.CSSProperties = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: '#f8fafc', fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif", padding: 24,
};
const signInFallbackCard: React.CSSProperties = {
  maxWidth: 480, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14,
  padding: '28px 32px', boxShadow: '0 12px 32px rgba(15,23,42,0.08)',
};
const signInFallbackBtn: React.CSSProperties = {
  background: '#1565c0', color: '#fff', border: 0, borderRadius: 8,
  padding: '10px 18px', fontWeight: 600, fontSize: 14, cursor: 'pointer',
};
const signInFallbackBtnGhost: React.CSSProperties = {
  background: '#f5f7fa', color: '#444', border: '1px solid #e5e7eb', borderRadius: 8,
  padding: '10px 18px', fontWeight: 600, fontSize: 14, cursor: 'pointer',
};
